import { useEffect, useRef, useState } from 'react';
import { Star } from 'lucide-react';

import { getRecommendedFits } from '../../services/toleranceService';

const SYSTEMS = [
  ['hole-basis', 'Hole basis'],
  ['shaft-basis', 'Shaft basis'],
];
const CATEGORIES = [
  ['clearance', 'Clearance'],
  ['transition', 'Transition'],
  ['interference', 'Interference'],
];

const SegmentedFilter = ({ label, options, value, onChange }) => (
  <div className="fitFilterGroup">
    <span>{label}</span>
    <div className="fitSegmented" aria-label={label}>
      {options.map(([optionValue, optionLabel]) => (
        <button
          key={optionValue}
          type="button"
          className={value === optionValue ? 'isSelected' : ''}
          aria-pressed={value === optionValue}
          onClick={() => onChange(optionValue)}
        >
          {optionLabel}
        </button>
      ))}
    </div>
  </div>
);

const chooseDefaultFit = (fits) => fits.find((fit) => fit.preferred) || fits[0] || null;

const RecommendedFits = ({ onSelect, preserveInitialSelection, selectedDesignation, selectedContent }) => {
  const [system, setSystem] = useState('hole-basis');
  const [category, setCategory] = useState('clearance');
  const [fits, setFits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const preserveInitialSelectionRef = useRef(preserveInitialSelection);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError('');

    getRecommendedFits({ system, category }, { signal: controller.signal })
      .then((response) => {
        if (active) setFits(response.fits ?? []);
      })
      .catch((requestError) => {
        if (!active || requestError?.code === 'ERR_CANCELED') return;
        setFits([]);
        setError('Recommended fits could not be loaded. Please try again.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [system, category]);

  useEffect(() => {
    if (loading || error) return;
    if (preserveInitialSelectionRef.current && selectedDesignation) {
      preserveInitialSelectionRef.current = false;
      return;
    }
    if (fits.length === 0) return;
    const selectedIsVisible = fits.some((fit) => fit.designation === selectedDesignation);
    if (!selectedIsVisible) onSelect(chooseDefaultFit(fits));
  }, [error, fits, loading, onSelect, selectedDesignation]);

  const handleSelect = (fit) => {
    onSelect(fit);
  };

  return (
    <section className="recommendedFits" aria-labelledby="recommended-fits-title">
      <header className="fitContentIntro">
        <p>ISO 286 reference</p>
        <h2 id="recommended-fits-title">Recommended Fits</h2>
        <span>Choose a curated commonly used combination to evaluate in the Fit Calculator.</span>
        <small>Preferred means the combination is marked preferred in the curated source; every listed combination is present in the commonly used fits table.</small>
      </header>

      <div className="fitFilters">
        <SegmentedFilter label="Fit system" options={SYSTEMS} value={system} onChange={setSystem} />
        <SegmentedFilter label="Fit category" options={CATEGORIES} value={category} onChange={setCategory} />
      </div>

      <div className="recommendedFitsStatus" aria-live="polite">
        {loading && <p>Loading recommended fits…</p>}
        {error && <p role="alert">{error}</p>}
        {!loading && !error && fits.length === 0 && <p>No recommended fits match these filters.</p>}
      </div>

      {selectedContent}

      {!loading && !error && fits.length > 0 && (
        <ul className="recommendedFitsList">
          {fits.map((fit) => (
            <li key={`${fit.designation}-${fit.order}`}>
              <button
                type="button"
                className={selectedDesignation === fit.designation ? 'isSelected' : ''}
                aria-pressed={selectedDesignation === fit.designation}
                onClick={() => handleSelect(fit)}
              >
                <span>
                  <strong>{fit.designation}</strong>
                  <small>{category} · {system.replace('-', ' ')}</small>
                </span>
                {fit.preferred && (
                  <span className="recommendedFitsPreferred">
                    <Star size={14} aria-hidden="true" /> Preferred
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default RecommendedFits;
